// src/components/Intro/intro.js
import React, { useEffect, useState, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FaFacebook, FaLinkedin, FaInstagram } from "react-icons/fa";

import {
  t as translate,
  normalizeLang,
  getLangFromPathname,
} from "../../i18n";

import darkModeImage from "../../assets/arttu.webp";
import lightModeImage from "../../assets/Arttu new white.png";
import lightVideo from "../../assets/light.mp4";
import darkVideo from "../../assets/header_video6.mp4";
import lightPoster from "../../assets/light_poster.webp";
import darkPoster from "../../assets/dark_poster.webp";
import { useSiteContent } from "../../content/SiteContentContext";

const Intro = ({ lang: langProp } = {}) => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const reduceMotion = useReducedMotion();
  const siteContent = useSiteContent();
  const contentMedia = siteContent?.media?.intro || {};
  const contentSocials = siteContent?.settings?.socials || {};
  // Home välittää normaalisti kielen propina.
  // Jos Introa käytetään joskus ilman lang-propia,
  // päätellään kieli suoraan selaimen oikeasta URL:sta.
  const pathname =
    typeof window !== "undefined"
      ? window.location.pathname
      : "/";
  const lang =
    typeof langProp === "string" && langProp
      ? normalizeLang(langProp)
      : getLangFromPathname(pathname);
  // Käytetään yhteistä i18n-helperiä, jotta WordPressistä haetut
  // tekstioverridet näkyvät myös introssa ilman uutta buildia.
  const t = useCallback(
    (key, vars) => translate(key, lang, vars),
    [lang]
  );
  // Some-linkit. WordPress voi korvata URL:t ilman uutta React-buildia.
  const socials = [
    {
      Icon: FaLinkedin,
      href: contentSocials.linkedin || "https://www.linkedin.com/in/arttu-stenius/",
      label: "LinkedIn",
    },
    {
      Icon: FaInstagram,
      href: contentSocials.instagram || "https://www.instagram.com/arttustenius.sivut/",
      label: "Instagram",
    },
    {
      Icon: FaFacebook,
      href: contentSocials.facebook || "https://www.facebook.com/arttustenius.sivut/",
      label: "Facebook",
    },
  ];
  useEffect(() => {
    // Intro ei pakota teemaa päälle.
    // Navbar hoitaa teeman localStoragen
    // ja html.dark-luokan kautta.
    const updateTheme = () => {
      setIsDarkMode(
        document.documentElement.classList.contains("dark")
      );
    };
    // Tarkistetaan nykyinen teema heti.
    updateTheme();
    const observer = new MutationObserver(() => {
      updateTheme();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  const poster = isDarkMode
    ? contentMedia.darkPoster || darkPoster
    : contentMedia.lightPoster || lightPoster;
  const bgVideo = isDarkMode
    ? contentMedia.darkVideo || darkVideo
    : contentMedia.lightVideo || lightVideo;
  const portrait = isDarkMode
    ? contentMedia.darkPortrait || darkModeImage
    : contentMedia.lightPortrait || lightModeImage;
  return (
    <section
      id="intro"
      className="
        relative
        min-h-[calc(100svh-4rem)]
        flex
        items-center
        justify-center
        overflow-hidden
        px-4
        py-4
      "
    >
      {/* Tausta:
          reduced motion -> poster,
          muuten video
      */}
      {!reduceMotion ? (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          key={isDarkMode ? "dark" : "light"}
          className="
            absolute
            inset-0
            w-full
            h-full
            object-cover
            saturate-110
          "
          aria-hidden="true"
          disablePictureInPicture
        >
          <source
            src={bgVideo}
            type="video/mp4"
          />
        </video>
      ) : (
        <img
          src={poster}
          alt=""
          className="
            absolute
            inset-0
            w-full
            h-full
            object-cover
            saturate-110
          "
          aria-hidden="true"
        />
      )}
      {/* Kontrastikerrokset luettavuuteen */}
      <div
        className={`absolute inset-0 ${
          isDarkMode
            ? "bg-black/20"
            : "bg-white/5"
        }`}
        aria-hidden="true"
      />
      <div
        className={`absolute inset-0 ${
          isDarkMode
            ? "bg-gradient-to-t from-black/85 via-black/35 to-transparent"
            : "bg-gradient-to-t from-white/65 via-white/10 to-transparent"
        }`}
        aria-hidden="true"
      />
      {/* Glow-blobit */}
      <motion.div
        aria-hidden="true"
        className="
          absolute
          -top-24
          -left-24
          w-72
          h-72
          sm:w-[28rem]
          sm:h-[28rem]
          rounded-full
          blur-3xl
          opacity-40
          bg-lime-400/30
        "
        initial={{
          scale: 0.9,
        }}
        animate={{
          scale: 1.05,
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          repeatType: "mirror",
          ease: "easeInOut",
        }}
      />
      <motion.div
        aria-hidden="true"
        className="
          absolute
          -bottom-28
          -right-28
          w-72
          h-72
          sm:w-[30rem]
          sm:h-[30rem]
          rounded-full
          blur-3xl
          opacity-35
          bg-sky-400/25
        "
        initial={{
          scale: 0.95,
        }}
        animate={{
          scale: 1.05,
        }}
        transition={{
          duration: 7,
          repeat: Infinity,
          repeatType: "mirror",
          ease: "easeInOut",
        }}
      />
      {/* Some-ikonit desktop */}
      <motion.div
        className="
          hidden
          sm:flex
          absolute
          left-6
          top-1/2
          -translate-y-1/2
          z-20
          flex-col
          gap-6
        "
        initial={{
          x: -80,
          opacity: 0,
        }}
        animate={{
          x: 0,
          opacity: 1,
        }}
        transition={{
          duration: 1,
        }}
      >
        {socials.map(
          (
            {
              Icon,
              href,
              label,
            },
            i
          ) => (
            <motion.a
              key={i}
              whileHover={{
                scale: 1.25,
                rotate: 6,
              }}
              className="
                text-white/80
                hover:text-lime-400
                transition
              "
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={label}
              title={label}
            >
              <Icon size={26} />
            </motion.a>
          )
        )}
      </motion.div>
      {/* Keskisisältö */}
      <div
        className="
          relative
          z-10
          flex
          flex-col
          items-center
          text-center
          px-6
          w-full
          max-w-5xl
          mx-auto
        "
      >
        {/* Profiilikuva */}
        <motion.div
          className="-mt-8 sm:mt-0"
        >
          <motion.img
            src={portrait}
            alt={t("intro.portraitAlt")}
            className="
              w-44
              h-44
              xs:w-48
              xs:h-48
              sm:w-72
              sm:h-72
              md:w-72
              md:h-72
              rounded-full
              object-cover
              border-4
              border-white/90
              shadow-[0_0_60px_rgba(132,204,22,0.55)]
            "
            initial={{
              scale: 0.8,
              opacity: 0,
            }}
            animate={{
              scale: 1,
              opacity: 1,
            }}
            transition={{
              duration: 1,
            }}
            loading="eager"
            decoding="async"
          />
        </motion.div>
        {/* Proof chips */}
        <motion.div
          className="
            mt-3
            flex
            flex-wrap
            gap-2
            justify-center
          "
          initial={{
            opacity: 0,
            y: 10,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 0.25,
            duration: 0.6,
          }}
        >
          {[
            "intro.chips.custom",
            "intro.chips.reactWp",
            "intro.chips.seoConversion",
            "intro.chips.fullStack",
          ].map((key) => (
            <span
              key={key}
              className="
                px-3
                py-1
                rounded-full
                text-[11px]
                sm:text-sm
                font-semibold
                bg-white/10
                text-white/90
                border
                border-white/15
                backdrop-blur
              "
            >
              {t(key)}
            </span>
          ))}
        </motion.div>
        {/* Nimi + rooli */}
        <motion.p
          className="
            mt-2
            text-white/85
            text-xs
            sm:text-sm
            md:text-base
            font-semibold
            tracking-wide
          "
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 0.4,
            duration: 0.6,
          }}
        >
          {t("intro.eyebrow")}
        </motion.p>
        {/* Sivun varsinainen H1 */}
        <motion.h1
          className="
            mt-2
            text-white
            font-black
            tracking-tight
            text-[2.15rem]
            leading-[1.05]
            sm:text-5xl
            sm:leading-none
            md:text-7xl
            max-w-5xl
          "
          initial={{
            opacity: 0,
            y: 30,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 0.5,
            duration: 0.8,
          }}
        >
          {t("intro.heading")}
        </motion.h1>
        {/* Arvolupaus */}
        <motion.p
          className="
            mt-3
            text-lime-400
            text-[0.95rem]
            leading-snug
            sm:text-base
            md:text-2xl
            font-semibold
            tracking-wide
            max-w-3xl
          "
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          transition={{
            delay: 0.85,
          }}
        >
          {t("intro.valueProp")}
        </motion.p>
        {/* Lisärivi */}
        <motion.p
          className="
            hidden
            sm:block
            mt-2
            text-white/85
            text-sm
            sm:text-base
            md:text-lg
            max-w-3xl
            leading-relaxed
          "
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          transition={{
            delay: 1,
          }}
        >
          {t("intro.subline")}
        </motion.p>
        {/* CTA */}
        <motion.div
          className="
            mt-3
            flex
            flex-row
            gap-2
            sm:gap-4
            items-center
            justify-center
            w-full
          "
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 1.2,
            duration: 0.6,
          }}
        >
          <a
            href="#works"
            className="
              px-3
              py-2
              sm:px-8
              sm:py-3
              rounded-lg
              sm:rounded-xl
              text-xs
              sm:text-base
              bg-lime-500
              text-black
              font-bold
              whitespace-nowrap
              shadow-[0_0_30px_rgba(132,204,22,0.75)]
              hover:shadow-[0_0_70px_rgba(132,204,22,0.95)]
              transition
              transform
              hover:-translate-y-[1px]
            "
          >
            {t("intro.cta.works")}
          </a>
          <a
            href="#contact"
            className="
              px-3
              py-2
              sm:px-8
              sm:py-3
              rounded-lg
              sm:rounded-xl
              text-xs
              sm:text-base
              border-2
              border-lime-400
              text-white
              whitespace-nowrap
              hover:bg-lime-400
              hover:text-black
              transition
              transform
              hover:-translate-y-[1px]
            "
          >
            {t("intro.cta.offer")}
          </a>
        </motion.div>
        {/* Some-ikonit mobiilissa */}
        <div
          className="
            mt-3
            flex
            sm:hidden
            gap-6
            justify-center
          "
        >
          {socials.map(
            (
              {
                Icon,
                href,
                label,
              },
              i
            ) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                title={label}
                className="
                  text-white/80
                  hover:text-lime-400
                  transition
                "
              >
                <Icon size={26} />
              </a>
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default Intro;